import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import {
  Loader2, ArrowLeft, Trophy, Sparkles, RefreshCw, ShieldAlert, HelpCircle,
  Scale, CheckCircle2, AlertTriangle, Wallet, Info, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";

const COMPARISON_ROWS = [
  ["personal_fit", "Kesesuaian dengan Anda"],
  ["capital_need", "Kebutuhan modal"],
  ["capital_risk", "Risiko modal"],
  ["speed_first_revenue", "Kecepatan pendapatan pertama"],
  ["margin", "Potensi margin"],
  ["repeat", "Potensi repeat order"],
  ["acquisition", "Kemudahan mendapatkan customer"],
  ["operational", "Kompleksitas operasional"],
  ["inventory_risk", "Risiko inventory"],
  ["scalability", "Potensi berkembang"],
  ["owner_dependency", "Ketergantungan waktu/tenaga pemilik"],
  ["goal_fit", "Kesesuaian dengan tujuan Anda"],
];

const LABEL_STYLE = {
  "Sangat Baik": "bg-brand text-white",
  "Baik": "bg-brand-accent text-white",
  "Sedang": "bg-brand-amber/15 text-brand-amber",
  "Perlu Perhatian": "bg-red-100 text-red-700",
};
const CONF_STYLE = { "Tinggi": "bg-brand text-white", "Sedang": "bg-brand-amber text-white", "Rendah": "bg-slate-300 text-slate-700" };

function Chip({ value, active }) {
  return <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${LABEL_STYLE[value] || "bg-slate-200 text-slate-600"} ${active ? "ring-2 ring-brand/30" : ""}`}>{value || "-"}</span>;
}

export default function Comparison() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [winner, setWinner] = useState(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [wRes, vRes] = await Promise.all([
          api.get("/winner").catch(() => ({ data: null })),
          api.get("/validation").catch(() => ({ data: null })),
        ]);
        setWinner(wRes.data || null);
        setReady(vRes.data?.status === "completed" && !!vRes.data?.deep);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const generate = async () => {
    setGenerating(true);
    setError("");
    try {
      const { data } = await api.post("/winner/generate");
      setWinner(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Gagal menentukan Business Winner.");
      toast.error("Gagal menentukan Business Winner.");
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return <DashboardLayout><div className="flex min-h-[50vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-brand-accent" /></div></DashboardLayout>;
  }

  if (generating) {
    return (
      <DashboardLayout>
        <div className="mx-auto max-w-xl text-center py-20 animate-fade-up" data-testid="winner-generating">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-brand/10 text-brand"><Loader2 className="h-8 w-8 animate-spin" /></div>
          <h2 className="mt-6 font-heading text-xl font-bold text-ink">Membandingkan kedua ide…</h2>
          <p className="mt-3 text-ink-soft leading-relaxed">IDEbisnis AI sedang menimbang kekuatan, risiko, dan kemudahan eksekusi kedua ide untuk menentukan mana yang paling layak Anda jalankan lebih dulu.</p>
        </div>
      </DashboardLayout>
    );
  }

  // Not ready (validation incomplete) and no winner yet
  if (!winner && !ready) {
    return (
      <DashboardLayout>
        <div className="mx-auto max-w-2xl text-center animate-fade-up" data-testid="winner-need-validation">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-brand/10 text-brand"><Scale className="h-8 w-8" /></div>
          <h1 className="mt-6 font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-ink">Selesaikan Validasi Dulu</h1>
          <p className="mt-4 text-base text-ink-soft leading-relaxed">Perbandingan & Business Winner membutuhkan hasil Validasi Mendalam kedua ide Anda.</p>
          <Button onClick={() => navigate("/validation")} data-testid="go-validation" className="mt-6 h-12 bg-brand hover:bg-brand-hover px-6">Ke Validasi</Button>
        </div>
      </DashboardLayout>
    );
  }

  // Ready, no winner yet -> generate CTA
  if (!winner) {
    return (
      <DashboardLayout>
        <div className="mx-auto max-w-2xl text-center animate-fade-up" data-testid="winner-generate-cta">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-brand/10 text-brand"><Trophy className="h-8 w-8" /></div>
          <h1 className="mt-6 font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-ink">Bandingkan 2 Ide & Temukan Pemenang</h1>
          <p className="mt-4 text-base text-ink-soft leading-relaxed">IDEbisnis AI akan membandingkan kedua ide tervalidasi Anda secara menyeluruh dan merekomendasikan satu yang paling layak dijalankan lebih dulu.</p>
          {error && <div className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="winner-error">{error}</div>}
          <Button onClick={generate} data-testid="generate-winner-btn" className="mt-6 h-12 bg-brand hover:bg-brand-hover px-6 text-base"><Sparkles className="mr-1.5 h-5 w-5" /> Tentukan Business Winner</Button>
        </div>
      </DashboardLayout>
    );
  }

  // Results
  const names = (winner.ideas || []).map((i) => i.name);
  const wi = winner.winner_index;
  return (
    <DashboardLayout>
      <div className="mx-auto max-w-3xl animate-fade-up" data-testid="winner-results">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate("/validation")} className="inline-flex items-center gap-1.5 text-sm text-ink-muted hover:text-brand transition-colors" data-testid="winner-back"><ArrowLeft className="h-4 w-4" /> Hasil Validasi</button>
          <Button variant="outline" onClick={generate} data-testid="regenerate-winner" className="border-borderc gap-1.5"><RefreshCw className="h-4 w-4" /> Buat Ulang</Button>
        </div>

        <h1 className="mt-4 font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-ink">Perbandingan 2 Ide Bisnis Anda</h1>
        <p className="mt-3 text-base text-ink-soft leading-relaxed">Keduanya layak dipertimbangkan, tetapi memiliki kekuatan, risiko, dan kebutuhan eksekusi yang berbeda.</p>

        {/* VS header */}
        <div className="mt-6 grid grid-cols-[1fr_auto_1fr] items-stretch gap-2 sm:gap-3" data-testid="vs-header">
          {[0, 1].map((idx) => (
            <div key={idx} className={`rounded-2xl border p-4 ${idx === wi ? "border-brand bg-brand/5" : "border-borderc bg-white"}`}>
              <span className="font-mono text-xs font-semibold text-brand-accent">IDE #{idx + 1}</span>
              {idx === wi && <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-brand px-2 py-0.5 text-[10px] font-bold text-white"><Trophy className="h-3 w-3" /> WINNER</span>}
              <h3 className="mt-1 font-heading text-sm sm:text-base font-bold text-ink leading-snug">{names[idx]}</h3>
            </div>
          ))}
          <div className="flex items-center justify-center"><span className="font-heading text-sm font-extrabold text-ink-muted">VS</span></div>
        </div>

        {/* Comparison table */}
        <section className="mt-6 rounded-2xl border border-borderc bg-white p-4 sm:p-6" data-testid="comparison-table">
          <div className="space-y-1">
            {COMPARISON_ROWS.map(([key, label]) => {
              const pair = winner.comparison?.[key] || [];
              return (
                <div key={key} className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 border-b border-borderc/60 py-2.5 last:border-0" data-testid={`cmp-${key}`}>
                  <div className="flex justify-start"><Chip value={pair[0]} active={wi === 0} /></div>
                  <div className="px-1 text-center text-[11px] font-medium text-ink-muted leading-tight">{label}</div>
                  <div className="flex justify-end"><Chip value={pair[1]} active={wi === 1} /></div>
                </div>
              );
            })}
          </div>
          <p className="mt-3 text-[11px] text-ink-muted">Label menunjukkan seberapa menguntungkan/aman aspek tersebut bagi kondisi Anda — bukan skor pasti.</p>
        </section>

        {/* Trade-off */}
        <section className="mt-6 rounded-2xl border border-borderc bg-white p-5 sm:p-6" data-testid="tradeoff-section">
          <div className="flex items-center gap-2"><Scale className="h-5 w-5 text-brand" /><h2 className="font-heading text-lg font-semibold text-ink">Trade-off Utama</h2></div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div className="rounded-xl border border-borderc bg-surface p-4">
              <div className="text-xs font-bold text-brand-accent">IDE #1 lebih unggul</div>
              <ul className="mt-2 list-disc pl-5 text-sm text-ink-soft space-y-1">{(winner.tradeoffs?.idea1_better || []).map((t, i) => <li key={i}>{t}</li>)}</ul>
              {winner.tradeoffs?.idea1_tradeoff && <p className="mt-2 text-xs text-ink-muted">Konsekuensi memilih IDE #1: {winner.tradeoffs.idea1_tradeoff}</p>}
            </div>
            <div className="rounded-xl border border-borderc bg-surface p-4">
              <div className="text-xs font-bold text-brand-accent">IDE #2 lebih unggul</div>
              <ul className="mt-2 list-disc pl-5 text-sm text-ink-soft space-y-1">{(winner.tradeoffs?.idea2_better || []).map((t, i) => <li key={i}>{t}</li>)}</ul>
              {winner.tradeoffs?.idea2_tradeoff && <p className="mt-2 text-xs text-ink-muted">Konsekuensi memilih IDE #2: {winner.tradeoffs.idea2_tradeoff}</p>}
            </div>
          </div>
        </section>

        {/* Business Winner */}
        <section className="mt-6 rounded-2xl border-2 border-brand bg-brand/5 p-5 sm:p-7" data-testid="business-winner">
          <div className="flex items-center gap-2 text-brand"><Trophy className="h-6 w-6" /><h2 className="font-heading text-xl font-extrabold">Business Winner</h2></div>
          <p className="mt-3 text-sm text-ink-soft leading-relaxed">
            Berdasarkan profil Anda, hasil validasi personal, karakter model bisnis, kebutuhan modal, risiko, dan kemudahan eksekusi, ide yang saat ini paling layak untuk Anda validasi dan jalankan terlebih dahulu adalah:
          </p>
          <div className="mt-3 rounded-xl bg-white border border-brand/30 p-4 font-heading text-lg font-extrabold text-brand" data-testid="winner-name">{winner.winner_name}</div>

          <div className="mt-5">
            <h3 className="font-heading text-sm font-bold text-ink">Mengapa Ide Ini Menang</h3>
            <ul className="mt-2 space-y-2">
              {(winner.winner_reasons || []).map((r, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-ink-soft"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand" /><span>{r}</span></li>
              ))}
            </ul>
          </div>

          {winner.confidence && (
            <div className="mt-5 flex flex-wrap items-center gap-2 rounded-lg bg-white/70 p-3" data-testid="winner-confidence">
              <span className="text-xs font-semibold text-ink-muted">Tingkat keyakinan:</span>
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${CONF_STYLE[winner.confidence]}`}>{winner.confidence}</span>
              <span className="text-xs text-ink-muted">{winner.confidence_note}</span>
            </div>
          )}
        </section>

        {/* Runner up */}
        {winner.runner_up_reason && (
          <section className="mt-6 rounded-2xl border border-borderc bg-white p-5 sm:p-6" data-testid="runner-up-section">
            <h2 className="font-heading text-lg font-semibold text-ink">Mengapa Ide Lain Belum Menjadi Pilihan Utama</h2>
            <p className="mt-2 text-sm text-ink-soft leading-relaxed">{winner.runner_up_reason}</p>
          </section>
        )}

        {/* Winner risks */}
        <section className="mt-6 rounded-2xl border border-borderc bg-white p-5 sm:p-6" data-testid="winner-risks-section">
          <div className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-brand-amber" /><h2 className="font-heading text-lg font-semibold text-ink">Risiko Terbesar Pemenang</h2></div>
          <ul className="mt-3 list-disc pl-6 text-sm text-ink-soft space-y-1">{(winner.winner_risks || []).map((r, i) => <li key={i}>{r}</li>)}</ul>
        </section>

        {/* Assumptions */}
        <section className="mt-6 rounded-2xl border border-borderc bg-white p-5 sm:p-6" data-testid="assumptions-section">
          <div className="flex items-center gap-2"><HelpCircle className="h-5 w-5 text-brand-accent" /><h2 className="font-heading text-lg font-semibold text-ink">Sebelum Mengeluarkan Modal Lebih Besar</h2></div>
          <ul className="mt-3 list-disc pl-6 text-sm text-ink-soft space-y-1">{(winner.assumptions || []).map((a, i) => <li key={i}>{a}</li>)}</ul>
        </section>

        {/* Capital note */}
        {winner.capital_note && (
          <section className="mt-6 flex items-start gap-2 rounded-2xl border border-brand-accent/30 bg-brand/5 p-5" data-testid="capital-note">
            <Wallet className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
            <div><h3 className="font-heading text-sm font-bold text-ink">Jaga Modal Anda</h3><p className="mt-1 text-sm text-ink-soft leading-relaxed">{winner.capital_note}</p></div>
          </section>
        )}

        {/* Next step */}
        <div className="mt-8 rounded-2xl border border-borderc bg-white p-5 text-center">
          <p className="text-sm text-ink-soft">Langkah berikutnya: susun rencana eksekusi untuk menguji pemenang.</p>
          <Button onClick={() => navigate("/blueprint")} data-testid="next-blueprint-cta" className="mt-3 h-12 bg-brand hover:bg-brand-hover px-6 text-base">
            Lanjut ke Business Blueprint <ArrowRight className="ml-1 h-5 w-5" />
          </Button>
          <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-ink-muted"><Info className="h-3.5 w-3.5" /> Ini alat bantu keputusan, bukan jaminan sukses. Uji dengan biaya kecil sebelum modal besar.</p>
        </div>
      </div>
    </DashboardLayout>
  );
}
